import { BaseLayer } from '.'
import type { Feature, FeatureCollection, Point } from 'geojson'
import { api } from '@/api'
import shipGlbUrl from '@/assets/ship.glb?url'
import {
  Cartesian2,
  Cartesian3,
  Color,
  DistanceDisplayCondition,
  HorizontalOrigin,
  LabelStyle,
  ConstantPositionProperty,
  GeoJsonDataSource,
  Entity,
  LabelGraphics,
  NearFarScalar,
  ModelGraphics,
  ConstantProperty,
  HeadingPitchRoll,
  Transforms,
  Cartographic,
  Math as CMath,
  BillboardGraphics,
} from 'cesium'
import type { AtcsShipDynamic } from '@/api/Api'
import { Subscriber } from 'cesium-extends'
import { nanoid } from 'nanoid'
import ShipPopup from '@/views/Map/Popup/ShipPopup.vue'
import { clameToGround } from '../utils'
import shipIconUri from '@/assets/ship.svg?url'
export class DynamicShipLayer extends BaseLayer {
  layername: string = '动态船只'
  key: string = 'dynamicShip'
  lasttime?: Date

  constructor() {
    super()
    this.once('mounted', (viewer) => {
      this._subscriber = new Subscriber(viewer, { pickResult: { enable: true } })
      this._subscriber.addExternal((_e, result) => {
        const entity = result?.id
        if (!(entity instanceof Entity) || !this.datasource.entities.contains(entity)) return
        const position = entity.position?.getValue()
        if (!position) return
        const cartographic = Cartographic.fromCartesian(position)
        this.popupCtrl.popup({
          viewer,
          id: entity.id,
          lnglat: [cartographic.longitude, cartographic.latitude].map(CMath.toDegrees) as [
            number,
            number,
          ],
          Com: ShipPopup,
          properties: entity.properties?.getValue(),
        })
      }, 'LEFT_CLICK')
    })
  }

  private async loadGeojson(
    isUpdate?: boolean
  ): Promise<FeatureCollection<Point, Omit<AtcsShipDynamic, 'longitude' | 'latitude'>>> {
    const res = await api.ship.shipControllerGetDynamicList({
      after: isUpdate && this.lasttime ? this.lasttime.toISOString() : undefined,
    })
    const featureCollection: FeatureCollection<
      Point,
      Omit<AtcsShipDynamic, 'longitude' | 'latitude'>
    > = {
      type: 'FeatureCollection',
      features: [],
    }
    let lastTime = 0
    res.data.forEach((el) => {
      const { longitude, latitude, ...properties } = el
      const feature: Feature<Point, Omit<AtcsShipDynamic, 'longitude' | 'latitude'>> = {
        type: 'Feature',
        id: properties.shipId ?? nanoid(),
        geometry: {
          type: 'Point',
          coordinates: [longitude, latitude].map(Number),
        },
        properties: properties,
      }

      featureCollection!.features.push(feature)
      lastTime = Math.max(new Date(properties.updateTime).valueOf(), lastTime)
    })
    this.lasttime = new Date(lastTime)
    return featureCollection
  }
  async render() {
    const geojson = await this.loadGeojson()
    await this.datasource.load(geojson)
    // assignHeight(this.datasource.entities.values, this._viewer!)
    this.datasource.entities.values.forEach((entity) => {
      this.renderEntity(entity)
    })
    setTimeout(() => {
      this.update()
    }, 2000)
  }
  private isUpdating = true
  /**
   * 更新动态
   */
  async update() {
    this.isUpdating = true
    const geojson = await this.loadGeojson(true)
    const updateEntities = Array<Entity>()
    /**更新 */
    geojson.features = geojson.features.filter((f) => {
      const entity = this.datasource.entities.getById(String(f.id))
      if (!entity) return true
      const coordinates = f.geometry.coordinates
      const [lng, lat] = coordinates
      entity.position = new ConstantPositionProperty(Cartesian3.fromDegrees(lng, lat))
      if (entity.properties) {
        for (const [key, value] of Object.entries(f.properties)) {
          entity.properties[key] = value
        }
      }
      updateEntities.push(entity)
      // entity.point!.color = new ConstantProperty(Color.YELLOW) // 检查哪些船只被修改
      return false
    })
    const db = await GeoJsonDataSource.load(geojson)
    clameToGround([...db.entities.values, ...updateEntities], this._viewer!)
    db.entities.values.forEach((entity) => {
      this.renderEntity(entity)
      this.datasource.entities.add(entity)
    })

    setTimeout(() => {
      if (this.isUpdating) this.update()
    }, 2000)
  }
  stopUpdate() {
    this.isUpdating = false
  }
  /**entity样式渲染 */
  private renderEntity(entity: Entity) {
    entity.billboard = new BillboardGraphics({
      image: shipIconUri,
      disableDepthTestDistance: 3000000,
      scaleByDistance: new NearFarScalar(1e3, 1, 3e4, 0.2),
      distanceDisplayCondition: new DistanceDisplayCondition(1000),
    })
    // entity.point = new PointGraphics({
    //   pixelSize: 8,
    //   color: Color.LIGHTGREEN,
    //   distanceDisplayCondition: new DistanceDisplayCondition(1000),
    // })
    entity.label = new LabelGraphics({
      font: `16px sans-serif`,
      showBackground: true,
      style: LabelStyle.FILL,
      backgroundColor: Color.fromCssColorString('#67ADDF'),
      pixelOffset: new Cartesian2(0, -20),
      scale: 1,
      scaleByDistance: new NearFarScalar(3e3, 1, 1e4, 0.55),
      text:
        entity.properties?.getValue()?.shipinfo?.aisShipName ??
        (entity.properties?.getValue().shipinfo?.mmsiCode
          ? 'mmsi:' + entity.properties?.getValue().shipinfo?.mmsiCode
          : '未知'),
      horizontalOrigin: HorizontalOrigin.CENTER,
      distanceDisplayCondition: new DistanceDisplayCondition(0, 10000),
      disableDepthTestDistance: 5e3,
    })
    entity.model = new ModelGraphics({
      uri: shipGlbUrl,
      distanceDisplayCondition: new DistanceDisplayCondition(0, 1000),
      scale: 1,
    })
    const feature = entity.properties?.getValue()
    const rotate = (feature.shipCourse / 180) * Math.PI
    const hpr = new HeadingPitchRoll(rotate, 0, 0)
    entity.orientation = new ConstantProperty(
      Transforms.headingPitchRollQuaternion(entity.position!.getValue()!, hpr)
    )
  }
}
